import React, {
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'querystring';

import { colors } from '@prisjakt/ui-base';
import Logo from '@prisjakt/ui-logo';
import { AppContext } from '@prisjakt/ui-app-context';
import { withViewport } from '@prisjakt/ui-viewport';
import { Hideable } from '@prisjakt/ui/hideable';
import { TonalButton, ButtonTone } from '@prisjakt/ui/button/variants';
import Clickable from '@prisjakt/ui-clickable';
import SearchBar from '../../components/search-bar';
import Viewport, { ViewportContext } from '../../components/viewport';
import headerActions from './actions';
import searchActions from '../search/actions/search';
import menuActions from '../navigation/actions/desktop';
import mobileMenuActions from '../navigation/actions/mobile-menu';
import NavigationMenu from '../navigation/index';
import MobileMenuButton from '../navigation/components/mobile-menu-button';
import NoScroll from '../../components/no-scroll';
import Suggestions from '../search-suggestions';
import Changelog from '../changelog/desktop-wrapper';
import { SearchBarMobile } from './components/search-bar-mobile';

import { useChangelogPositionExperiment } from '../../hooks/experiments';

import { AppState } from '../../reducers';

import * as S from './styles';
import { TooltipPlacement } from 'src/components/tooltip-new';

type OwnProps = {
  viewport: {
    top: number;
  };
};

type StateProps = {
  mobileMenuOpen?: boolean;
  megaMenuOpen?: boolean;
  showSearchBar: boolean;
  me: AppState['areas']['account']['me'];
  isAuthenticated: boolean;
};

type DispatchProps = {
  searchFocus: () => void;
  searchBlur: () => void;
  closeMobileMenu: () => void;
  toggleMenu: (state: { open: boolean }) => void;
  setShowSearchBar: (state: boolean) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const Header = ({
  mobileMenuOpen,
  megaMenuOpen = false,
  toggleMenu,
  closeMobileMenu,
  searchFocus,
  searchBlur,
  viewport,
  setShowSearchBar,
  showSearchBar,
  me,
  isAuthenticated,
}: Props) => {
  const location = useLocation();
  const history = useHistory();

  const megaMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const inputElement = useRef<HTMLInputElement | null>(null);
  const searchWrapperElement = useRef<HTMLDivElement | null>(null);

  const { t, market } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);

  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const renderChangelogInHeader = useChangelogPositionExperiment();

  const onSearchInputRefUpdateCallback = useCallback(node => {
    // Updates ref to current value if element changes, as when e.g. mobile search field is expanded.
    inputElement.current = node;
  }, []);

  const onSearchWrapperRefUpdateCallback = useCallback(node => {
    // Updates ref to current value if element changes, as when e.g. mobile search field is expanded.
    searchWrapperElement.current = node;
  }, []);

  const setQueryAndUpdateSuggestions = useCallback((q: string) => {
    setQuery(q);

    if (q.length > 0) {
      setShowSuggestions(true);
    }
  }, []);

  // Update search query on URL change
  useEffect(() => {
    const qs = queryString.parse(location.search.substr(1));
    const queryParam = qs.search || qs.q || '';
    setQuery(Array.isArray(queryParam) ? queryParam[0] : queryParam);
  }, [location.search]);

  const toggleMegaMenu = useCallback(
    e => {
      // Prevent event from triggering an "outside click" for the menu
      e.stopPropagation();

      toggleMenu({ open: !megaMenuOpen });
    },
    [toggleMenu, megaMenuOpen]
  );

  const toggleSearchBar = useCallback(() => {
    setShowSearchBar(!showSearchBar);

    if (mobileMenuOpen) {
      closeMobileMenu();
    }
  }, [mobileMenuOpen, closeMobileMenu, setShowSearchBar, showSearchBar]);

  const clearQuery = useCallback(() => setQuery(''), []);

  const closeSearchBar = useCallback(() => {
    setShowSearchBar(false);
    clearQuery();
  }, [clearQuery, setShowSearchBar]);

  const onCloseMegaMenu = useCallback(() => {
    if (megaMenuButtonRef.current) {
      megaMenuButtonRef.current.focus();
    }
  }, []);

  const searchFocused = /search=/.test(location.search);
  const isFrontpage = location.pathname === '/';
  const isSearchPage = location.pathname.indexOf('/search') === 0;

  const isSidebarOpen = mobileMenuOpen;
  const isScrolledDown = viewport && viewport.top > 90;
  const isMobileSearchOpen =
    (showSearchBar || isSearchPage || (isFrontpage && !isScrolledDown)) &&
    !isSidebarOpen;
  const menuButtonTitle = megaMenuOpen
    ? t('symbols', 'symbol.close')
    : t('header', 'categories.title');

  useEffect(() => {
    // Hide the search bar when changing route on mobile
    if (history.action === 'PUSH' && !searchFocused && !isSearchPage) {
      closeSearchBar();
    }
  }, [location, history, searchFocused, isSearchPage, closeSearchBar]);

  return (
    <>
      <S.FixedMargin mobileSearchOpen={isMobileSearchOpen} />
      <S.HeaderBar mobileSearchOpen={isMobileSearchOpen}>
        <S.Wrapper style={{ zIndex: showSuggestions ? 'unset' : 2 }}>
          <S.MenuColumn data-test="MobileMenuColumn">
            <S.MenuItem>
              <MobileMenuButton />
            </S.MenuItem>
          </S.MenuColumn>

          <S.LogoColumn>
            <S.MegaMenuItem
              title={menuButtonTitle}
              placement={TooltipPlacement.RIGHT}
            >
              <S.MegaMenuButton
                type="button"
                onClick={toggleMegaMenu}
                aria-label={menuButtonTitle}
                active={megaMenuOpen}
                aria-expanded={megaMenuOpen}
                aria-haspopup
                ref={megaMenuButtonRef}
                data-test="MegaMenuToggleButton"
              >
                {megaMenuOpen ? (
                  <S.CloseMegaMenuIcon size={30} />
                ) : (
                  <S.MegaMenuIcon size={30} />
                )}
              </S.MegaMenuButton>
            </S.MegaMenuItem>

            <S.LogoLink
              to="/"
              onClick={() => mobileMenuOpen && closeMobileMenu()}
            >
              <Logo
                market={market.code}
                companyName={t('meta', 'market.logo-alt')}
                styling="v2-light"
              />
            </S.LogoLink>
          </S.LogoColumn>

          <S.SearchColumn tablet desktop>
            <SearchBar
              query={query}
              setQuery={setQueryAndUpdateSuggestions}
              clearQuery={clearQuery}
              autoFocus={isFrontpage}
              onFocusChange={searchFocus}
              onBlurChange={searchBlur}
              onCloseSuggestions={() => setShowSuggestions(false)}
              onClick={() => setShowSuggestions(true)}
              handleSearchInputRefCallback={onSearchInputRefUpdateCallback}
              handleSearchWrapperRefUpdateCallback={
                onSearchWrapperRefUpdateCallback
              }
            />
          </S.SearchColumn>

          <S.RightColumn>
            {Boolean(market.showPromoText) && (
              <S.PromoText>{t('header', 'logo.promo-text')}</S.PromoText>
            )}
            <Viewport mobile>
              <Hideable visible={!isMobileSearchOpen}>
                <S.MenuItem>
                  <button
                    style={{ height: 50 }}
                    type="button"
                    onClick={toggleSearchBar}
                    aria-label={t('header', 'search.button')}
                  >
                    <S.SearchIcon size={30} />
                  </button>
                </S.MenuItem>
              </Hideable>
            </Viewport>

            {isDesktop && (
              <S.UserArea>
                {renderChangelogInHeader && <Changelog />}

                {isAuthenticated ? (
                  <S.AccountMenu
                    position="bottom"
                    grow="left"
                    toggleElement={
                      <S.MenuButton
                        variant="icon"
                        color="primary"
                        icon={<S.UserAvatar {...me} size={24} />}
                      />
                    }
                    toggleElementActiveStyles={{
                      background: colors.lightTheme.staticInformative700,
                    }}
                  />
                ) : (
                  <TonalButton
                    container={Clickable}
                    tone={ButtonTone.DARK}
                    containerProps={{ to: '/login' }}
                  >
                    {t('account', 'menu.not-logged-in-button')}
                  </TonalButton>
                )}
              </S.UserArea>
            )}
          </S.RightColumn>
        </S.Wrapper>

        <S.SearchOverlay active={showSuggestions}>
          <S.SearchBox visible={isMobileSearchOpen}>
            <S.SearchColumn mobile>
              <SearchBarMobile
                active={showSuggestions}
                onClose={() => {
                  setShowSuggestions(false);
                  searchBlur();
                }}
                query={query}
                setQuery={setQueryAndUpdateSuggestions}
                clearQuery={clearQuery}
                onFocusChange={searchFocus}
                onBlurChange={searchBlur}
                autoFocusOnExpand={showSearchBar && !isFrontpage}
                onCloseSuggestions={() => setShowSuggestions(false)}
                onOpenSuggestions={() => setShowSuggestions(true)}
                onClick={() => setShowSuggestions(true)}
                handleSearchInputRefCallback={onSearchInputRefUpdateCallback}
                handleSearchWrapperRefUpdateCallback={
                  onSearchWrapperRefUpdateCallback
                }
              />
            </S.SearchColumn>
          </S.SearchBox>

          <Suggestions
            visible={showSuggestions}
            query={query}
            setQuery={setQuery}
            onClose={() => setShowSuggestions(false)}
            updatedInputElement={inputElement}
            updatedSearchWrapperElement={searchWrapperElement}
          />
        </S.SearchOverlay>

        <NoScroll mobile enabled={showSuggestions} />
      </S.HeaderBar>

      <NavigationMenu onCloseMegaMenu={onCloseMegaMenu} />
    </>
  );
};

const mapStateToProps = (state: AppState) => ({
  mobileMenuOpen: state.areas.navigation.mobileMenu.open,
  megaMenuOpen: state.areas.navigation.megaMenu.open,
  showSearchBar: state.areas.header.showSearchBar,
  me: state.areas.account.me,
  isAuthenticated: state.areas.account.isAuthenticated,
});

const dispatchActions = {
  searchFocus: searchActions.searchFocus,
  searchBlur: searchActions.searchBlur,
  closeMobileMenu: mobileMenuActions.closeMenu,
  toggleMenu: menuActions.toggleMenu,
  setShowSearchBar: headerActions.setShowSearchBar,
};

export default compose(
  withViewport,
  connect(mapStateToProps, dispatchActions)
)(Header);
